const joinCellTexts = ({
    cell
  }) =>
  cell.texts.reduce((p, h) => `${p}${h.text}${h.separator}`, '');

const isNumeric = n => !isNaN(parseFloat(n)) && isFinite(n);
const isInteger = n => isNumeric(n) && Number.isInteger(parseFloat(n));
const isUndefined = input => typeof input === 'undefined';

const joinSymbols = ({
    word
  }) =>
  word.symbols
  .filter(h => h.text)
  .map(h => h.text)
  .join('');

const till = waitingFor =>
  waitingFor.then(result => ({
    result
  })).catch(error => ({
    error
  }));

/**
 * create a column label for sheet address, starting at 1 = A, 27 = AA etc..
 * @param {number} columnNumber the column number
 * @return {string} the address label
 */
const columnLabelMaker = (columnNumber, s) => {
  s =
    String.fromCharCode(((columnNumber - 1) % 26) + 'A'.charCodeAt(0)) +
    (s || '');
  return columnNumber > 26 ? columnLabelMaker(Math.floor((columnNumber - 1) / 26), s) : s;
};
const rangeMaker = ({
    rows,
    columns,
    rowOffset,
    columnOffset
  }) =>
  `${columnLabelMaker((columnOffset || 0) + 1)}${(rowOffset || 0) +
    1}:${columnLabelMaker((columnOffset || 0) + (columns || 1))}${(rowOffset ||
    0) + (rows || 1)}`;

const dummyRows = ({
    cells
  }) =>
  Array(cells.reduce((p, c) => Math.max(c.rowNumber + 1, p), 0)).fill(() => '');

const findIndexReverse = (arr, func) => {
  for (var x = arr.length - 1; x >= 0 && !func(arr[x], x, arr); x--);
  return x;
};
// check on args
const paramCheck = ({
  params,
  names,
  ignoreExtras
}) => {
  const missing = names.filter(f => !f.optional).filter(f => !params[f.name]);
  const unknown = ignoreExtras ? [] : Object
    .keys(params)
    .filter(f => !names.find(g => g.name === f));

  if (missing.length || unknown.length) {
    let error = missing.length ? 'missing args: ' + missing.join(',') : '';
    error += unknown.length ? 'unknown args: ' + unknown.join(',') : '';
    return {
      params,
      error,
    };
  } else {
    return {
      params,
    };
  }
};

/**
 * randBetween
 * get an random number between x and y
 * @param {number} min the lower bound
 * @param {number} max the upper bound
 * @return {number} the random number
 **/
const randBetween = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * get an arbitrary alpha string
 * @param {number} length of the string to generate
 * @return {string} an alpha string
 **/
const arbitraryString = function (length) {
  var s = '';
  for (var i = 0; i < length; i++) {
    s += String.fromCharCode(randBetween(97, 122));
  }
  return s;
};

/**
 * generate a run id based on current time
 */

const getRunBase = (d) => new Date(d).getTime().toString(32);
// some extra disambiguation if required
const getRunID = () => getRunBase(new Date()) + arbitraryString(6);
const getDateFromRunBase = (runBase) => new Date(parseInt(runBase, 32));
const getDateFromRunID = ({ runID, base = 32, size = 9 }) => runID ? new Date(parseInt(runID.slice(0, size), base)) : ''

const treeUntangleArray = ({
  item,
  arr,
  localizedKey
}) => {
  arr = arr || [];
  if (item) {
    arr.push({
      text: (localizedKey ? item[localizedKey] && item[localizedKey].name : item.name) || '',
      itemOrder: item.itemOrder
    });
    treeUntangleArray({
      item: item.Parent,
      arr,
      localizedKey
    });
  }
  return arr;
};


// flatten out a tree structure
const treeUntangle = ({
  item,
  localizedKey,
  keepOrder,
  separator
}) => {
  const text = treeUntangleArray({
    item,
    localizedKey
  }).map(f => f.text);
  return (keepOrder ? text : text.reverse()).filter(f => f).filter((f, i, a) => a.indexOf(f) === i).join(separator || '/');
};

const chunker = ({
  source,
  maxChunkSize
}) => {
  const chunks = [];
  let index = 0;
  while (index < source.length) {
    chunks.push(source.slice(index, maxChunkSize + index));
    index += maxChunkSize;
  }
  return chunks;
};

const getNearestFrameRateID = (frameRates, fps) => {
  const f = parseFloat(fps);
  if (!f || isNaN(f)) return null;

  // faster lookup
  const fm = frameRates.reduce((p, c) => {
    p[c.id.toString()] = {
      fps: parseFloat(c.frameRate),
      id: c.id,
      name: c.frameRate
    };
    return p;
  }, {});

  // find best match
  return Object.keys(fm).reduce((p, c) => {
    const cob = fm[c];
    return isUndefined(p) ? cob : (Math.abs(cob.fps - fps) < Math.abs(p.fps - fps) ? cob : p);
  }, undefined);
};

// get best match defintion format
const getMatchingDefinitionFormat = (definitionFormats, format) => (definitionFormats || []).find(d => d.name.toLowerCase() === format.toLowerCase());

// try to find best match aspect ratio description
const getNearestAspectRatioID = (aspectRatios, width, height) => {
  const f = parseFloat(width) / parseFloat(height);
  if (!f || isNaN(f)) return null;

  // faster lookup
  const fm = aspectRatios.reduce((p, c) => {
    p[c.id.toString()] = {
      wh: parseFloat(c.widthUnits / c.heightUnits),
      id: c.id,
      name: c.name
    };
    return p;
  }, {});

  // find best match
  return Object.keys(fm).reduce((p, c) => {
    const cob = fm[c];
    return isUndefined(p) ? cob : (Math.abs(cob.wh - f) < Math.abs(p.wh - f) ? cob : p);
  }, undefined);
};
export  {
  joinCellTexts,
  isNumeric,
  isInteger,
  joinSymbols,
  till,
  columnLabelMaker,
  rangeMaker,
  dummyRows,
  findIndexReverse,
  paramCheck,
  getRunID,
  treeUntangle,
  treeUntangleArray,
  chunker,
  getRunBase,
  getDateFromRunBase,
  getNearestFrameRateID,
  getMatchingDefinitionFormat,
  getNearestAspectRatioID,
  getDateFromRunID
};